// src/hooks/useClassEducator.js
//
// "Whose class is this?"
//
// An educator is looking at their own class, so the answer is their own
// profile — no lookup needed.
//
// A student or parent is looking at the class of whoever's educator code
// they hold. That's a lookup, and it has to be a document GET rather than a
// query: firestore.rules allows `list` on /users only to admins, so a child
// cannot search for their teacher. `educatorDirectory/{code}` exists for
// exactly this — one document, keyed by code, holding only the teacher's
// name. Looking up a code reveals a name and nothing else: no email, no
// student list, no profile access.
//
// A child with no code, or a code matching nobody, gets null. The caller
// then greets them by name instead of claiming they're in a class they
// aren't in.

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import useAccessLevel from "./useAccessLevel";
import { ROLE_KIND } from "../utils/accountRole";

// Directory reads are cached for the session. A student moving between
// Science, Math and Social Studies would otherwise re-read the same document
// on every page, and the answer cannot change mid-visit.
const cache = new Map();

export default function useClassEducator() {
  const { profile, roleKind, loading } = useAccessLevel();
  const [educator, setEducator] = useState(null);
  const [resolving, setResolving] = useState(true);

  const viewerIsEducator =
    roleKind === ROLE_KIND.EDUCATOR ||
    roleKind === ROLE_KIND.ADMIN ||
    roleKind === ROLE_KIND.DISTRICT;

  const code = profile?.educatorCode ? String(profile.educatorCode) : "";

  useEffect(() => {
    let cancelled = false;

    if (loading) return undefined;

    // An educator IS the class. Their own profile already has sirName and
    // lastName on it, so this costs nothing.
    if (viewerIsEducator) {
      setEducator(profile || null);
      setResolving(false);
      return undefined;
    }

    if (!code) {
      setEducator(null);
      setResolving(false);
      return undefined;
    }

    if (cache.has(code)) {
      setEducator(cache.get(code));
      setResolving(false);
      return undefined;
    }

    setResolving(true);

    getDoc(doc(db, "educatorDirectory", code))
      .then((snap) => {
        const found = snap.exists() ? snap.data() : null;
        cache.set(code, found);
        if (!cancelled) {
          setEducator(found);
          setResolving(false);
        }
      })
      .catch((err) => {
        // Non-fatal by design. The greeting falls back to the child's own
        // name, which is friendly rather than broken.
        console.warn("Educator lookup failed:", err?.code || err?.message);
        if (!cancelled) {
          setEducator(null);
          setResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loading, viewerIsEducator, code, profile]);

  return {
    educator,          // { sirName, lastName, displayName, … } or null
    viewer: profile || {},
    viewerIsEducator,
    loading: loading || resolving,
  };
}
